import React, { useRef, useEffect } from 'react';
import { getPlotPalette, onThemeChange } from '../theme/themeColors';

function TrainingLossPlot() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // DPI-aware sizing
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        const g = canvas.getContext('2d');
        g.scale(dpr, dpr);

        const pad = 40;
        const plotW = w - pad - 20;
        const plotH = h - pad - 20;
        const pts = 80;
        let animFrame;
        let p = getPlotPalette();
        const offThemeChange = onThemeChange(() => {
            p = getPlotPalette();
        });

        function drawLoss() {
            const t = performance.now() * 0.001;
            g.clearRect(0, 0, w, h);

            // Axes
            g.strokeStyle = `rgba(${p.axis}, 0.2)`;
            g.lineWidth = 0.8;
            g.beginPath();
            g.moveTo(pad, h - pad); g.lineTo(w - 15, h - pad);
            g.moveTo(pad, h - pad); g.lineTo(pad, 12);
            g.stroke();

            // Grid
            g.strokeStyle = `rgba(${p.axis}, 0.06)`;
            g.lineWidth = 0.4;
            for (let i = 1; i <= 4; i++) {
                const gy = h - pad - (i / 4) * plotH;
                g.beginPath(); g.moveTo(pad, gy); g.lineTo(w - 15, gy); g.stroke();
            }

            // Sweep: lines draw left-to-right over 6 seconds
            const cycle = (t % 6) / 6;
            const progress = cycle < 0.15 ? 0 : Math.min(1, (cycle - 0.15) / 0.7);
            const maxPt = progress * (pts - 1);

            // Train line
            g.beginPath();
            g.strokeStyle = `rgba(${p.s1}, 0.7)`;
            g.lineWidth = 2;
            let trainEnd = null;
            for (let i = 0; i <= maxPt && i < pts; i++) {
                const frac = i / (pts - 1);
                const val = 0.8 * Math.exp(-frac * 5) + 0.03 + Math.sin(i * 0.35) * 0.006;
                const px = pad + frac * plotW;
                const py = h - pad - val * plotH;
                i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
                trainEnd = { px, py, i };
            }
            g.stroke();

            // Train dot at leading edge
            if (trainEnd && trainEnd.i < pts - 1) {
                g.fillStyle = `rgba(${p.s1}, 0.9)`;
                g.beginPath(); g.arc(trainEnd.px, trainEnd.py, 4.5, 0, Math.PI * 2); g.fill();
            } else if (progress >= 0.99) {
                const fl = (pts - 1) / (pts - 1);
                const fv = 0.8 * Math.exp(-fl * 5) + 0.03 + Math.sin((pts - 1) * 0.35) * 0.006;
                const fpx = pad + fl * plotW;
                const fpy = h - pad - fv * plotH;
                g.fillStyle = `rgba(${p.s1}, 0.9)`;
                g.beginPath(); g.arc(fpx, fpy, 4.5, 0, Math.PI * 2); g.fill();
            }

            // Val line
            g.beginPath();
            g.strokeStyle = `rgba(${p.s2}, 0.6)`;
            g.lineWidth = 1.6;
            let valEnd = null;
            for (let i = 0; i <= maxPt && i < pts; i++) {
                const frac = i / (pts - 1);
                const n = Math.sin(i * 0.5) * 0.014 + Math.sin(i * 0.28) * 0.009;
                const val = 0.72 * Math.exp(-frac * 4.2) + 0.07 - n + frac * 0.07;
                const px = pad + frac * plotW;
                const py = h - pad - val * plotH;
                i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
                valEnd = { px, py, i };
            }
            g.stroke();

            // Val dot at leading edge
            if (valEnd && valEnd.i < pts - 1) {
                g.fillStyle = `rgba(${p.s2}, 0.85)`;
                g.beginPath(); g.arc(valEnd.px, valEnd.py, 3.5, 0, Math.PI * 2); g.fill();
            } else if (progress >= 0.99) {
                const fl = (pts - 1) / (pts - 1);
                const vn = Math.sin((pts - 1) * 0.5) * 0.014 + Math.sin((pts - 1) * 0.28) * 0.009;
                const vv = 0.72 * Math.exp(-fl * 4.2) + 0.07 - vn + fl * 0.07;
                const fpx = pad + fl * plotW;
                const fpy = h - pad - vv * plotH;
                g.fillStyle = `rgba(${p.s2}, 0.85)`;
                g.beginPath(); g.arc(fpx, fpy, 3.5, 0, Math.PI * 2); g.fill();
            }

            // Labels
            g.fillStyle = `rgba(${p.axis}, 0.5)`;
            g.font = '10px Inter, sans-serif';
            g.textAlign = 'center';
            g.fillText('Epochs', w / 2, h - 6);
            g.save(); g.translate(14, h / 2); g.rotate(-Math.PI / 2);
            g.fillText('Loss', 0, 0); g.restore();

            // Legend
            g.fillStyle = `rgba(${p.s1}, 0.7)`; g.fillRect(pad + 6, 22, 14, 3);
            g.fillStyle = `rgba(${p.axis}, 0.6)`; g.font = '9px Inter, sans-serif'; g.textAlign = 'left';
            g.fillText('Train', pad + 24, 26);
            g.fillStyle = `rgba(${p.s2}, 0.7)`; g.fillRect(pad + 68, 22, 14, 3);
            g.fillText('Val', pad + 86, 26);

            animFrame = requestAnimationFrame(drawLoss);
        }

        animFrame = requestAnimationFrame(drawLoss);

        return () => {
            offThemeChange();
            if (animFrame) cancelAnimationFrame(animFrame);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                border: '1px solid var(--plot-border)',
                display: 'block',
                background: 'var(--plot-bg)',
            }}
            aria-label="Training and validation loss over epochs"
        />
    );
}

export default TrainingLossPlot;
